import React, { useState, useEffect } from 'react'
import useSWR from 'swr'
import spektrixAuth from '@lib/spektrix/auth'
import SpektrixFetcher from '@lib/spektrix/fetcher'
import PreSalePrompt from './pre-sale-prompt'
import PriorityBookingLoginPrompt from './login-prompt'
import PriorityBookingMembershipRequired from './membership-required'
import PriorityBookingErrorModal from './error-modal'
import { showBookingError } from '@lib/booking-error-handler'

const EnhancedPriorityBookingHandler = ({ 
  event, 
  instance, 
  children, 
  onAuthStateChange 
}) => {
  const [authState, setAuthState] = useState({
    authenticated: false,
    user: null,
    loading: true
  })
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [hasAccessThroughPromo, setHasAccessThroughPromo] = useState(false)

  // Fetch basket to check for promo codes
  const { data: basketData, mutate: mutateBasket } = useSWR('/basket', SpektrixFetcher)

  // Check various states
  const now = new Date()
  const startSellingAt = instance?.start_selling_at ? new Date(instance.start_selling_at) : null
  const isPreSale = startSellingAt && now < startSellingAt && !instance?.is_on_sale
  const isPriorityBookingActive = event?.is_priority_booking_active
  const hasPriorityBookingStarted = event?.priority_booking_starts_at && 
    now >= new Date(event.priority_booking_starts_at)

  // Check if basket has a valid promo code
  useEffect(() => {
    if (basketData?.promoCode) {
      setHasAccessThroughPromo(true)
    }
  }, [basketData])

  // Set up the error handler override if needed
  useEffect(() => {
    if ((isPriorityBookingActive || isPreSale) && !window.__priorityBookingHandlerSet) {
      window.__priorityBookingHandlerSet = true
      window.__originalShowBookingError = window.showBookingError
      
      window.showBookingError = (error, options = {}) => {
        showBookingError(error, {
          ...options,
          onPriorityBookingError: () => {
            if (window.__activePriorityBookingHandler) {
              window.__activePriorityBookingHandler.setShowErrorModal(true)
            }
          }
        })
      }
    }

    return () => {
      if (window.__priorityBookingHandlerSet && window.__originalShowBookingError) {
        window.showBookingError = window.__originalShowBookingError
        window.__priorityBookingHandlerSet = false
        window.__originalShowBookingError = null
      }
    }
  }, [isPriorityBookingActive, isPreSale])

  // Check if user has active memberships/subscriptions
  const hasActiveSubscription = (user) => {
    if (!user || !user.subscriptions) {
      return false
    }
    
    const currentDate = new Date()
    return user.subscriptions.some(subscription => {
      if (subscription.endDate) {
        const endDate = new Date(subscription.endDate)
        return endDate > currentDate
      }
      
      if (subscription.startDate) {
        const startDate = new Date(subscription.startDate)
        return startDate <= currentDate
      }
      
      return true
    })
  }

  // Check auth status
  useEffect(() => {
    if (!isPriorityBookingActive && !isPreSale) {
      setAuthState({ authenticated: false, user: null, loading: false })
      return
    }
    
    spektrixAuth.checkAuthStatus().then(result => {
      const newAuthState = {
        authenticated: result.authenticated,
        user: result.user,
        loading: false
      }
      setAuthState(newAuthState)
      
      if (onAuthStateChange) {
        onAuthStateChange(newAuthState)
      }
    })

    const cleanup = spektrixAuth.onAuthChange((authData) => {
      const newAuthState = {
        authenticated: authData.authenticated,
        user: authData.user,
        loading: false
      }
      setAuthState(newAuthState)
      
      if (onAuthStateChange) {
        onAuthStateChange(newAuthState)
      }
    })

    return cleanup
  }, [isPriorityBookingActive, isPreSale, onAuthStateChange])

  // Register this component instance
  useEffect(() => {
    if (isPriorityBookingActive || isPreSale) {
      window.__activePriorityBookingHandler = { setShowErrorModal }
    }

    return () => {
      if (window.__activePriorityBookingHandler?.setShowErrorModal === setShowErrorModal) {
        window.__activePriorityBookingHandler = null
      }
    }
  }, [isPriorityBookingActive, isPreSale, setShowErrorModal])

  const handleLoginSuccess = (user) => {
    const newAuthState = {
      authenticated: true,
      user: user,
      loading: false
    }
    setAuthState(newAuthState)
    
    if (onAuthStateChange) {
      onAuthStateChange(newAuthState)
    }
  }

  const handlePromoCodeSuccess = () => {
    // Re-fetch basket to check promo code
    mutateBasket()
    setHasAccessThroughPromo(true)
  }

  // Determine if user has access
  const hasAccess = authState.authenticated && hasActiveSubscription(authState.user) || hasAccessThroughPromo

  // If not pre-sale and not priority booking, render children normally
  if (!isPreSale && !isPriorityBookingActive) {
    return children
  }

  // If still loading auth state, show loading
  if (authState.loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-2"></div>
        <span className="text-gray-600 dark:text-gray-400">Checking access...</span>
      </div>
    )
  }

  // Always render with error modal wrapper
  const renderWithErrorModal = (content) => (
    <>
      {content}
      <PriorityBookingErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        eventName={event?.name}
      />
    </>
  )

  // If in pre-sale period and no access
  if (isPreSale && !hasAccess) {
    return renderWithErrorModal(
      <PreSalePrompt
        eventName={event?.name}
        instanceStartSellingAt={instance?.start_selling_at}
        onLoginSuccess={handleLoginSuccess}
        onPromoCodeSuccess={handlePromoCodeSuccess}
      />
    )
  }

  // If priority booking is active and not authenticated
  if (isPriorityBookingActive && !authState.authenticated && !hasAccessThroughPromo) {
    return renderWithErrorModal(
      <PriorityBookingLoginPrompt 
        eventName={event?.name}
        onLoginSuccess={handleLoginSuccess}
      />
    )
  }

  // If authenticated but no subscription during priority booking
  if (isPriorityBookingActive && authState.authenticated && !hasActiveSubscription(authState.user) && !hasAccessThroughPromo) {
    return renderWithErrorModal(
      <PriorityBookingMembershipRequired 
        eventName={event?.name}
      />
    )
  }

  // User has access (either through membership or promo code)
  return renderWithErrorModal(children)
}

export default EnhancedPriorityBookingHandler